/**
 * defineXElement — Shared custom element base class and registration helper.
 *
 * Provides a base HTMLElement subclass that x-* custom elements extend.
 * Handles attribute observation, connected/disconnected lifecycle callbacks,
 * and delegation to the HtmlEcs registry/apply pipeline.
 */

/**
 * Resolve the base class for x-* elements. In the browser this is the real
 * `HTMLElement`. In Node / SSR / pre-bundle environments where `HTMLElement`
 * is undefined, fall back to a no-op stub so that `import 'enginex'` does
 * NOT throw `ReferenceError: HTMLElement is not defined` at module-load
 * time. The stub class is never registered or instantiated outside the
 * browser (`defineXElement` short-circuits when `customElements` is absent).
 */
const HTMLElementBase: typeof HTMLElement =
  typeof HTMLElement !== 'undefined'
    ? HTMLElement
    : (class {} as unknown as typeof HTMLElement);

/**
 * Base class for all x-* custom elements.
 * Tracks connected state and notifies the HtmlEcs mutation pipeline.
 */
export class XBaseElement extends HTMLElementBase {
  /** Whether this element is connected to the DOM. */
  _xConnected = false;

  connectedCallback(): void {
    this._xConnected = true;
  }

  disconnectedCallback(): void {
    this._xConnected = false;
  }
}

/**
 * Define and register a custom element class in the browser's CustomElementRegistry.
 * Safe to call multiple times — skips if already defined.
 *
 * @param tagName - The custom element tag name (e.g. 'x-entity')
 * @param elementClass - Optional custom class extending XBaseElement
 */
export function defineXElement(
  tagName: string,
  elementClass?: typeof XBaseElement,
): void {
  // Guard: only register in browser environment with customElements API
  if (typeof customElements === 'undefined') return;

  // Skip if already defined
  if (customElements.get(tagName)) return;

  const cls = elementClass ?? class extends XBaseElement {};
  customElements.define(tagName, cls);
}

/**
 * Register all built-in x-* custom elements.
 * Called once during HtmlEcsModule.init().
 */
export function registerAllCustomElements(): void {
  const tags = [
    // Core
    'x-scene', 'x-entity', 'x-prefab', 'x-instance',
    'x-assets', 'x-asset', 'x-script', 'x-virtualscreen',
    'x-xr', 'x-layer',
    // Primitives
    'x-box', 'x-sphere', 'x-plane', 'x-cylinder', 'x-cone',
    'x-torus', 'x-circle', 'x-ring',
    // Scene objects
    'x-light', 'x-camera', 'x-audio', 'x-terrain', 'x-model',
    // Subsystem elements
    'x-versona', 'x-particle', 'x-trail', 'x-water',
    'x-navmesh', 'x-nav-agent', 'x-decal',
    'x-weather', 'x-daynight', 'x-vegetation',
    'x-postprocess', 'x-fog', 'x-city',
    // Added elements — cover previously tag-less subsystems so authors can
    // discover them via tab-completion rather than memorizing comp keys.
    'x-splat', 'x-skybox', 'x-trigger', 'x-spawnpoint', 'x-crowd', 'x-hud',
  ];

  for (const tag of tags) {
    defineXElement(tag);
  }
}
